'use client';

import { Menu } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Link } from '@/i18n/navigation';
import { LocaleSwitcher } from '@/components/i18n/LocaleSwitcher';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics/ym';
import { SITE } from '@/lib/site-config';

const links = [
  { href: '/cases', key: 'cases' },
  { href: '/#process', key: 'process' },
  { href: '/#faq', key: 'faq' },
  { href: '/guides', key: 'guides' },
] as const;

export function Navbar() {
  const t = useTranslations('Navbar');
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur-md">
      <nav className="container mx-auto flex h-16 items-center justify-between px-4 md:h-18">
        <Link href="/" className="flex items-center gap-2 font-semibold tracking-tight">
          <img
            src="/logos/cortexit-mark-light.png"
            alt=""
            aria-hidden="true"
            className="h-7 w-7 dark:hidden"
          />
          <img
            src="/logos/cortexit-mark-dark.png"
            alt=""
            aria-hidden="true"
            className="hidden h-7 w-7 dark:block"
          />
          {SITE.brand}
        </Link>
        <ul className="hidden items-center gap-6 md:flex">
          {links.map((l) => (
            <li key={l.href}>
              <Link href={l.href} className="text-sm text-muted-foreground hover:text-foreground">
                {t(`links.${l.key}`)}
              </Link>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-3">
          <LocaleSwitcher />
          <ThemeToggle />
          <Link
            href="/#lead"
            onClick={() => track.ctaClick('navbar')}
            className={cn(buttonVariants({ size: 'sm' }), 'hidden md:inline-flex rounded-full')}
          >
            {t('cta')}
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              render={<Button variant="ghost" size="icon" className="md:hidden" />}
              aria-label={t('menu')}
            >
              <Menu className="h-5 w-5" />
            </SheetTrigger>
            <SheetContent side="right">
              <SheetHeader>
                <SheetTitle>{t('menu')}</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-1 px-4">
                {links.map((l) => (
                  <Link
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="rounded-md px-2 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    {t(`links.${l.key}`)}
                  </Link>
                ))}
              </nav>
              <SheetFooter>
                <Link
                  href="/#lead"
                  onClick={() => {
                    track.ctaClick('navbar');
                    setOpen(false);
                  }}
                  className={cn(buttonVariants({ size: 'sm' }), 'rounded-full')}
                >
                  {t('cta')}
                </Link>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
